"use client";

/**
 * Live storage-efficiency figures for the Web Index pages.
 *
 * WHY LIVE AND NOT A LITERAL. "under 25 KB per page" is a competitive claim, and a hardcoded one
 * silently becomes a lie the first time the embedder, the chunker or the payload shape changes.
 * This reads the running system through the same server-side rewrite the rest of the public index
 * data uses (/api/index/storage -> search_index /live/storage), so the browser never sees an
 * internal host and the number cannot drift from reality.
 *
 * UNITS ADAPT. Figures pick their own unit instead of printing 4,840,224,896. A stat nobody can
 * read at a glance is not a marketing asset. fmtBytes and fmtCount are exported and used elsewhere,
 * so both keep their full range even though this component no longer renders anything above KB.
 *
 * NO TOTALS. See the note on `stats` — this block deliberately publishes no figure describing how
 * large the index is.
 */

import { useEffect, useState } from "react";
import { fmtBytes } from "@/lib/format-stat";

const ENDPOINT = "/api/index/storage";

type StorageWeb = {
  pages?: number;
  chunks?: number;
  bytes?: number;
  chunks_per_page?: number;
  bytes_per_page?: number;
  bytes_per_page_disk?: number;
};
type StorageData = { web?: StorageWeb; total_bytes?: number; collections?: unknown[] };

/* Both formatters now live in @/lib/format-stat so every stat on the site rounds identically.
   Re-exported here because other components already import them from this path. */
export { fmtBytes, fmtCount } from "@/lib/format-stat";

export function StorageEfficiencyLive({ initial }: { initial?: StorageData | null }) {
  /* Seeded by the server so the figure is in the HTML on first paint. Before this the value
     arrived only after hydration, so every visitor saw a dash first and anything that does not
     run JavaScript — crawlers, link previews — never saw it at all. The client fetch below is
     kept: it refreshes a page that may have been cached for minutes. */
  const [data, setData] = useState<StorageData | null>(initial ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(ENDPOINT, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const web = data?.web;

  // Render the CLAIM only when the number backing it has actually arrived. A skeleton is honest;
  // a fallback literal would state a competitive figure this component could not verify.
  // RATIOS AND STRUCTURE ONLY — NO ABSOLUTE SCALE. This block used to carry "Pages held",
  // "Passages" and "Whole index", which stated how big the index currently is. The index is still
  // being built, and a total is the one figure on these pages that invites the comparison we lose:
  // any established index is orders of magnitude larger, so publishing a running total argues
  // against us and dates badly besides — every screenshot, cached copy and AI summary freezes a
  // number we have already grown past.
  //
  // What survives is the argument that does not depend on size. Kilobytes per page is a claim about
  // efficiency that holds at any scale and gets MORE impressive as the index grows. Passages per
  // page describes how material is filed. Collection count describes breadth of kind, not volume.
  /* Physical collections that hold web material. The feed also carries askFinz's own site index,
     which is not one of them. */
  const collectionCount = Array.isArray(data?.collections)
    ? data.collections.filter(
        (c) => !String((c as { collection?: unknown })?.collection ?? "").startsWith("askfinz_site_index"),
      ).length || null
    : null;

  // Add nothing here that reads as a total.
  const stats = [
    {
      label: "Per stored page",
      value: fmtBytes(web?.bytes_per_page_disk),
      sub: "everything needed to find and rank it",
    },
    {
      label: "Passages per page",
      value: web?.chunks_per_page ? web.chunks_per_page.toFixed(1) : "—",
      sub: "each one separately searchable",
    },
    {
      label: "Collections",
      /* Excludes `askfinz_site_index*` — the index of this site, not a library of the web. Same
         rule as getCollectionCount() in lib/live-stats.ts, which has always applied it. */
      value: collectionCount != null ? String(collectionCount) : "—",
      sub: "grouped into the pages listed here",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border hairline-strong bg-foreground/10 sm:grid-cols-3">
      {stats.map((s) => (
        <div key={s.label} className="bg-card p-5">
          <div className="font-mono text-2xl font-semibold tabular-nums md:text-3xl">
            {failed ? "—" : s.value}
          </div>
          <div className="mt-1 text-xs uppercase tracking-[0.12em] text-foreground/50">{s.label}</div>
          <div className="mt-1.5 text-[11px] leading-relaxed text-foreground/55">{s.sub}</div>
        </div>
      ))}
    </div>
  );
}
